import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CATEGORIES } from "@/constants/categories";
import { ImageDropzone } from "@/components/admin/ImageDropzone";
import { useCategoryImages, useInvalidateSiteContent } from "@/hooks/useSiteContent";
import { updateCategoryImage } from "@/lib/api";

function apiErrorDetail(err: unknown, fallback: string): string {
  if (typeof err === "object" && err !== null && "response" in err) {
    const response = (err as { response?: { data?: { detail?: unknown } } }).response;
    const detail = response?.data?.detail;
    if (typeof detail === "string") return detail;
  }
  if (typeof err === "object" && err !== null && "code" in err && (err as { code?: string }).code === "ECONNABORTED") {
    return "Request timed out — check that the backend is running";
  }
  return fallback;
}

export const AdminCategoriesPage = () => {
  const { data: categoryImages, isLoading, isError } = useCategoryImages();
  const { invalidateCategories } = useInvalidateSiteContent();
  const [uploadingSlug, setUploadingSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [savedSlug, setSavedSlug] = useState<string | null>(null);

  const imageBySlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of categoryImages ?? []) {
      if (item.image_url) map.set(item.slug, item.image_url);
    }
    return map;
  }, [categoryImages]);

  const handleUpload = async (slug: string, results: { url: string; public_id?: string }[]) => {
    const result = results[0];
    if (!result) return;

    setError(null);
    setSavedSlug(null);
    setUploadingSlug(slug);
    try {
      await updateCategoryImage(slug, result.url, result.public_id ?? null);
      await invalidateCategories();
      setSavedSlug(slug);
    } catch (err: unknown) {
      setError(apiErrorDetail(err, `Failed to save image for ${slug}`));
    } finally {
      setUploadingSlug(null);
    }
  };

  return (
    <div data-testid="admin-categories-page">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Category images</h1>
        <p className="text-sm text-vg-gray mt-1">
          Upload card images for each storefront category. Changes appear on the landing page.
        </p>
      </div>

      {isLoading && (
        <p className="text-sm text-vg-gray mb-4">Loading existing images…</p>
      )}

      {isError && (
        <p className="text-sm text-amber-700 mb-4">
          Could not load existing images. You can still upload new ones below.
        </p>
      )}

      {error && (
        <p className="text-sm text-red-600 mb-4" data-testid="admin-form-error">
          {error}
        </p>
      )}

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm text-left">
          <thead className="border-b border-black/10 bg-[#f8fafc]">
            <tr>
              <th className="px-4 py-3 font-medium w-32">Preview</th>
              <th className="px-4 py-3 font-medium w-48">Category</th>
              <th className="px-4 py-3 font-medium">Upload</th>
              <th className="px-4 py-3 font-medium w-24 text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {CATEGORIES.map((category) => {
              const imageUrl = imageBySlug.get(category.slug);
              const isUploading = uploadingSlug === category.slug;

              return (
                <tr
                  key={category.slug}
                  className="border-b border-black/5 last:border-0 align-top"
                  data-testid={`admin-category-${category.slug}`}
                >
                  <td className="px-4 py-4">
                    <div className="w-24 aspect-[4/3] border border-black/10 bg-[#f3f4f6] overflow-hidden shrink-0">
                      {imageUrl ? (
                        <img
                          src={imageUrl}
                          alt={category.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[10px] text-vg-gray text-center px-1">
                          No image
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-medium">{category.name}</div>
                    <div className="text-xs text-vg-gray mt-0.5 font-mono">{category.slug}</div>
                  </td>
                  <td className="px-4 py-4 min-w-[280px]">
                    <ImageDropzone
                      folder="categories"
                      disabled={isUploading}
                      label={
                        imageUrl
                          ? "Drop a new image to replace, or click to browse"
                          : "Upload category image"
                      }
                      onError={setError}
                      onUploaded={(results) => handleUpload(category.slug, results)}
                    />
                  </td>
                  <td className="px-4 py-4 text-right">
                    {savedSlug === category.slug && (
                      <span className="text-xs text-green-700">Saved</span>
                    )}
                    {isUploading && (
                      <span className="text-xs text-vg-gray">Uploading…</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-8">
        <Link
          to="/"
          className="inline-flex items-center h-10 px-4 border border-black/15 text-sm hover:bg-[#f8fafc]"
        >
          View storefront
        </Link>
      </div>
    </div>
  );
};

export default AdminCategoriesPage;
